<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { groups } from 'src/stores/lists/groups';
import { app } from 'src/stores/session/native-bridge';
	//Helpers
	import { getDevicesWithItsChannels, real_id } from '../../../../helpers/interface_helpers';

	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { checkMode } from '../../../../helpers/mode_switch_helpers';
	import {
		checkSharedChannels,
		unshareAllChannels,
		type SharedChannelData,
	} from '../../../../helpers/profile_change_shared_helper';
	//Types
	import type { DeviceUIDeviceSidePanelView, ViewCategory } from 'src/types/DeviceUI';
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Container, Modal, Select, Text } from '@Shelly/shelly-design-system';
	import { resetDrawer, resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { shouldShowViewSelectionOnModeChange } from 'src/components/Sidepanel/view-selection-config';
	import { saveViewForAllChannels } from 'src/components/Sidepanel/view-helpers';
	import DeviceViewSelectionModal from 'src/components/Sidepanel/DeviceViewSelectionModal.svelte';
	//Components
	import ProfileChangeSharedModal from './ProfileChangeSharedModal.svelte';

	export let device;
	const id = real_id(device.id);

	const options = ['cover', 'switch'];
	const i18n_options = {
		cover: $format('label_cover', { default: 'cover' }),
		switch: $format('label_switch', { default: 'switch' }),
	};
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;

	let value = options[options.indexOf(settings?.sys?.device?.profile)];
	let cache = value;
	let loading = false;
	let showModal: boolean = false;
	let showConfirmation: boolean = false;
	let showSharedModal: boolean = false;
	let showViewSelectionModal: boolean = false;
	let viewCategory: ViewCategory | null = null;
	let selectedView: DeviceUIDeviceSidePanelView | null = null;
	let sharedChannelsData: SharedChannelData[] = [];
	let isSaveDisabled = true;

	$: isSaveDisabled = loading || cache === value;

	const selectMode = (e) => {
		for (const key in i18n_options) {
			if (i18n_options[key] === e.detail) {
				value = key;
			}
		}
	};

	const handleSave = async (device) => {
		if (cache === value) return;

		const newMode = value === 'cover' ? 'roller' : 'relay';
		viewCategory = shouldShowViewSelectionOnModeChange(device.type, newMode);

		if (viewCategory) {
			showViewSelectionModal = true;
			return;
		}

		await proceedToConfirmation(device);
	};

	const onViewSelectionComplete = (event: CustomEvent<{ view: DeviceUIDeviceSidePanelView }>) => {
		showViewSelectionModal = false;
		selectedView = event.detail?.view ?? null;
		proceedToConfirmation(device);
	};

	const proceedToConfirmation = async (device) => {
		const channelsData = await checkSharedChannels(device);

		if (channelsData.length > 0) {
			sharedChannelsData = channelsData;
			showSharedModal = true;
			return;
		}

		const devicesWithChannels = getDevicesWithItsChannels(device);

		const concatenatedDevices: string[] = Object.values($groups).reduce<string[]>(
			(res, obj: any) => {
				res = res.concat(obj.devices);
				return res;
			},
			[],
		);

		const uniqueDevices = [...new Set(concatenatedDevices)];

		const inGroup = uniqueDevices.filter((deviceId: string) =>
			devicesWithChannels.includes(deviceId),
		);

		if (inGroup.length !== 0) {
			showModal = true;
		} else {
			showConfirmation = true;
		}
	};

	const sendReq = async () => {
		if (cache === value) return;
		loading = true;
		showModal = false;
		showConfirmation = false;
		showSharedModal = false;

		await unshareAllChannels(sharedChannelsData);

		const params = {
			id: device.channel,
			name: options[options.indexOf(value)],
		};

		const res: SettingRequest = await gen2Request(device, 'Shelly.SetProfile', params);

		const response = onSettingsResponse(res, device);

		if (response?.isok) {
			if ($app) {
				resetDrawerClose();
			} else {
				resetDrawer();
			}
			settings.sys.device.profile = options[options.indexOf(value)];
			cache = value;
		} else {
			selectedView = null;
			value = cache;
		}
		await checkMode(device.id, settings);
		if (selectedView) {
			saveViewForAllChannels(device.id, selectedView);
			selectedView = null;
		}
		loading = false;
	};
</script>

<Select
	class="mb-s"
	size="m"
	value={i18n_options[value]}
	options={Object.values(i18n_options)}
	icon={'fas fa-info-circle'}
	info={$format('text_modal_remove_device_from_group_description', {
		default:
			'Warning: Changing device profile would restart the device and clear all existing schedules and actions.',
	})}
	on:change={(e) => selectMode(e)}
	id="select-n58vk8m"
	aria-label={$format('device_profile', { default: 'Device profile' })}
/>

{#if showViewSelectionModal}
	<DeviceViewSelectionModal
		bind:showModal={showViewSelectionModal}
		{device}
		category={viewCategory}
		on:save={onViewSelectionComplete}
	/>
{/if}

{#if sharedChannelsData.length > 0}
	<ProfileChangeSharedModal bind:showModal={showSharedModal} on:confirm={sendReq} />
{/if}

<Button
	class="mt-s"
	{loading}
	disabled={isSaveDisabled}
	size="m"
	width="full"
	on:click={() => handleSave(device)}>{$format('button_save')}</Button
>

<Modal
	on:close={() => (showConfirmation = false)}
	showModal={showConfirmation}
	title={$format('label_confirmation', {
		default: 'Confirmation',
	})}
	backdropClose={true}
>
	<Container slot="content" class="p-s" padding="0">
		<Text>
			{$format('text_confirmation_change_cover_profile', {
				default:
					'Changing the device profile will delete all webhooks and schedules on the device. Are you sure you want to proceed?',
			})}
		</Text>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => (showConfirmation = false)}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="primary"
			on:click={sendReq}>{$format('button_confirm', { default: 'Confirm' })}</Button
		>
	</div>
</Modal>

<Modal
	on:close={() => (showModal = false)}
	{showModal}
	title={$format('modal_title_remove_device_from_group', {
		default: 'Remove device from groups',
	})}
	backdropClose={true}
>
	<Container slot="content" class="p-s" padding="0">
		<Text>
			{$format('text_modal_remove_device_from_group_description', {
				default: 'The device will be removed from group after changing the profile.',
			})}
		</Text>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => (showModal = false)}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="danger"
			on:click={sendReq}>{$format('button_remove', { default: 'Remove' })}</Button
		>
	</div>
</Modal>
